/**
 * Utils.js
 * 
 * Integración de getDecimalFromFraction. 
 * Para conversión de strings fraccionarios a decimales.
 * 
 * @version 4.0
 * @author Daniel Lepe
 * @date Feb 10th, 2025.
 * 
 * Actualización con versión de StoicFiles.
 * 
 * @version 3.0
 * @author Daniel Lepe
 * @date January 25th, 2025.
 * 
 * Some handy functions
 * Added function toHumanReadable; it turns camelCase and snake_case
 * into "Human Case";
 * 
 * @version 2.1
 * @author Daniel Lepe
 * @date January 24th, 2025.
 * 
 * Added quick function arrayToObject, swalMSG and mixedMSG
 * 
 * @version 2.0
 * @author Daniel Lepe
 * @date June 30th, 2023
 */
import swal from 'sweetalert'
import swal2 from 'sweetalert2'
import _ from 'lodash'
import axios from 'axios'

import { translation as transEn } from './en.js'
import { translation as transEs } from './es.js'

const Toast = swal2.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        grow: 'column',
        timer: 8000
    }),
    arrayToObject = (arr, key) => {
        return _.keyBy(arr, key);
    },
    toHumanReadable = (str) => {
        // Handle snake_case
        if (str.includes('_')) {
            return str
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }

        // Handle camelCase
        return str
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    },
    confirmFirst = async ({ text, title, goLabel, cancelLabel }) => {
        if (!goLabel) {
            goLabel = 'Continuar';
        }
        if (!cancelLabel) {
            cancelLabel = 'Cancelar';
        }
        if (!title) {
            title = '¿Seguro que deseas continuar?';
        }
        let response = await swal({
            icon: 'info',
            title,
            text,
            buttons: {
                cancel: cancelLabel,
                danger: goLabel
            }
        })
        return response
    },
    notify = (e) => {
        console.error(e)
        Toast.fire({
            type: 'warning',
            title: e
        });
    },
    toastMSG = (msg, classTXT) => {
        if (!!msg)
            Toast.fire({
                type: (classTXT) ? classTXT : 'primary',
                title: msg
            });
    },
    swalMSG = (msg, classTXT) => {
        if (!!msg) {
            swal({
                title: msg,
                text: null,
                icon: (classTXT) ? classTXT : 'warning',
                buttons: {
                    ok: 'OK',
                }
            })
        }
    },
    mixedMSG = (status, msg, classTXT) => {
        if (!!msg) {
            if (!status) {
                swal({
                    title: msg,
                    text: null,
                    icon: (classTXT) ? classTXT : 'warning',
                    buttons: {
                        ok: 'OK',
                    }
                })
            } else {
                toastMSG(msg, classTXT)
                // mobiscroll.snackbar({
                //     display: 'top',
                //     theme: 'ios',
                //     message: msg,
                //     color: (classTXT) ? classTXT : 'warning',
                //     duration: 3000
                // })
            }
        }
    },
    swalResponse = (response) => {
        if (!_.isObjectLike(response))
            throw 'Error de conectividad';
        mixedMSG(response.status, response.msg, response.class);
    },
    extractForm = (formArchetype) => {
        let ret = {},
            formArchetypeArr;
        if (_.isFunction(formArchetype))
            formArchetypeArr = formArchetype()
        else
            formArchetypeArr = _.cloneDeep(formArchetype)
        _.forEach(formArchetypeArr, (field) => {
            if (field != null && _.isArray(field)) {
                _.forEach(field, (subField) => {
                    ret[subField.key] = ('default' in subField)
                        ? subField.default
                        : null;
                });
            } else if (field != null && !!field.key) {
                ret[field.key] = ('default' in field) ? field.default : null;
            }
        });
        return ret;
    },
    generarNombreClaseCSS = (cadena) => {
        let nombreClase = cadena;
        // Elimina los espacios en blanco al principio y al final
        nombreClase = nombreClase.trim();
        // Reemplaza los espacios en blanco por guiones
        nombreClase = nombreClase.replace(/\\s+/g, '-');
        // Elimina los caracteres no válidos
        nombreClase = nombreClase.replace(/[^a-zA-Z0-9\\-]/g, '');
        // Asegura que el primer carácter no sea numérico
        nombreClase = nombreClase.replace(/^[0-9]/, '');
        return nombreClase;
    },
    keyMatrix = {
        // MATRIX DEFINITION
        'en-US': '_en',
        'es-MX': '_es'
    },
    translate = (valueOrigin, label) => {
        try {
            const userLang = window.localStorage.getItem('language') || navigator.language || navigator.userLanguage;
            if (typeof valueOrigin === 'string') {
                let trans;
                if (userLang === 'en-US' || userLang === 'en-CA') {
                    trans = transEn;
                } else {
                    trans = transEs;
                }
                return trans[valueOrigin] || valueOrigin;
            } else if (typeof valueOrigin === 'object' && valueOrigin != null) {
                // LABEL VALIDATION
                if (typeof label === 'undefined') throw 'label not recieved!';
                const containingProp = label + keyMatrix[userLang];
                if ((containingProp) in valueOrigin) {
                    return valueOrigin[containingProp];
                } else {
                    return label
                }
            }
        } catch (e) {
            console.error
            return ''
        }
    },
    objectToUrlString = (obj, delimiter, equal) => {
        try {
            delimiter = (delimiter || ':')
            equal = (equal || '=')
            let str = Object.entries(obj)
                .map(([key, value]) => {
                    if (value === true)
                        value = '1';
                    else if (value === false)
                        value = '0';
                    else if (value === null)
                        value = 'NULL';
                    else if (_.isObjectLike(value))
                        value = objectToUrlString(value, '||', '++')
                    else
                        value += '';
                    const valueFixed = (value).replace(/[ ]/g, '_');
                    return (valueFixed) ? `${encodeURIComponent(key)}${equal}${encodeURIComponent(valueFixed)}` : null;
                })
                .join(delimiter);
            return str;
        } catch (e) {
            console.error('objectToUrlString.error', e)
        }
    },
    downloadFile = async (fileName, urlLocation) => {
        try {
            const url = window.URL.createObjectURL(new Blob([await axios.get(urlLocation, {
                responseType: 'blob',
            })]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    },
    getDecimalFromFraction = (fraction) => {
        try {
            let testExp = /(\d+[ ])?\d+\/\d+/,
                decimal = 0.0;

            if (testExp.test(fraction)) {
                console.log('fraction', fraction)
                let tieneEnterosExp = /(\d+[ ])\d+\/\d+/;

                let fractionTrimmed = fraction.trim().replace(/[ ]+/g, ' ').replace(/\/+/g, '/');

                if (!tieneEnterosExp.test(fractionTrimmed))
                    fractionTrimmed = '0 ' + fractionTrimmed;

                let numeros = fractionTrimmed.split(' '),
                    parteEntera = parseInt(numeros[0]),
                    fractionSplitted = numeros[1].split('/'),
                    numerador = parseInt(fractionSplitted[0]),
                    denominador = parseInt(fractionSplitted[1]);

                return parteEntera + numerador / denominador;
            } else {
                console.log('decimal', parseFloat(fraction))
                decimal = parseFloat(fraction);
                return decimal;
            }
        } catch (e) {
            console.warn('getDecimalFromFraction:' + fraction + ' Not computable!');
            return NaN;
        }
    }

export {
    notify,
    toastMSG,
    swalMSG,
    confirmFirst,
    mixedMSG,
    arrayToObject,
    extractForm,
    swalResponse,
    generarNombreClaseCSS,
    translate,
    toHumanReadable,
    objectToUrlString,
    downloadFile,
    getDecimalFromFraction,
    Toast
}